/**
 * FDA Form 483 Service
 * Fetches FDA inspection data (inspection-centric, spec compliant)
 */

import { FDA_MESSAGES } from '../constants/validations';
import {
  FDA_API_BASE_URL,
  FDA_API_HEADERS,
  MAX_INSPECTIONS,
  API_TIMEOUT
} from '../constants';

/* ---------------- Types ---------------- */

export interface FDAInspectionRecord {
  FEINumber: string;
  LegalName: string;
  AddressLine1: string;
  AddressLine2: string | null;
  City: string;
  StateCode: string;
  State: string;
  ZipCode: string;
  CountryCode: string;
  CountryName: string;
  InspectionID: string;
  InspectionEndDate: string;
  FiscalYear: string;
  PostedCitations: string;
  Classification: string;
  ClassificationCode: string;
  ProjectArea: string;
  ProductType: string;
  AdditionalDetails: string | null;
  FMD145Date: string | null;
  FirmProfile: string;
}

export interface FDAInspectionResponse {
  statuscode: number;
  message: string;
  totalrecordcount: number;
  resultcount: number;
  result: FDAInspectionRecord[];
}

export interface CDMOInspectionSummary {
  companyName: string;
  inspections: FDAInspectionRecord[];
  lastFetched: number;
}

/* ---------------- Cache Config ---------------- */

const inspectionCache = new Map<string, CDMOInspectionSummary>(); // L1
const CACHE_DURATION = 1000 * 60 * 60 * 24; // 24 hours

const LOCAL_STORAGE_KEY = 'fda_inspection_cache_v1'; // L2

/* ---------------- API Config ---------------- */


/* ---------------- LocalStorage Helpers (L2) ---------------- */

function loadLocalCache(): Record<string, CDMOInspectionSummary> {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveLocalCache(cache: Record<string, CDMOInspectionSummary>) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cache));
}

function isCacheValid(lastFetched: number): boolean {
  return Date.now() - lastFetched < CACHE_DURATION;
}


/* ---------------- FDA Fetch (API only) ---------------- */

async function fetchFromFDA(
  companyName: string,
  feiNumbers?: string[]
): Promise<CDMOInspectionSummary> {

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

  let response;
  try {
    // Build filters object - prefer FEI number(s) if available, otherwise use company name
    const filters: any = {};

    if (feiNumbers && feiNumbers.length > 0) {
      // Convert FEI strings to numbers for the API payload
      const parsed = feiNumbers
        .map(f => String(f).trim())
        .filter(Boolean)
        .map(f => Number(f))
        .filter(n => !Number.isNaN(n));

      if (parsed.length > 0) {
        filters.FEINumber = parsed;
      } else {
        filters.LegalName = [companyName];
      }
    } else {
      filters.LegalName = [companyName];
    }

    response = await fetch(FDA_API_BASE_URL, {
      method: 'POST',
      headers: FDA_API_HEADERS,
      signal: controller.signal,
      body: JSON.stringify({
        returntotalcount: true,
        sort: 'InspectionEndDate',
        sortorder: 'desc',
        filters,
        rows: MAX_INSPECTIONS,
        columns: []
      })
    });
  } catch (error: any) {
    if (error.name === 'AbortError') {
      throw new Error(FDA_MESSAGES.TIMEOUT_ERROR);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw new Error(
      FDA_MESSAGES?.API_ERROR(response?.status, response?.statusText)
    );
  }

  const data: FDAInspectionResponse = await response.json();

  const inspections = data.result
    .sort(
      (a, b) =>
        new Date(b.InspectionEndDate).getTime() -
        new Date(a.InspectionEndDate).getTime()
    )
    .slice(0, MAX_INSPECTIONS);

  return {
    companyName,
    inspections,
    lastFetched: Date.now()
  };
}

/* ---------------- Main Fetch (L1 → L2 → API) ---------------- */

export async function fetchFDAInspections(
  companyName: string,
  feiNumber?: string | number | Array<number | string> | null
): Promise<CDMOInspectionSummary> {

  // Normalize FEI input into an array of trimmed strings
  let feiArray: string[] = [];
  if (Array.isArray(feiNumber)) {
    feiArray = feiNumber.map(String).map(s => s.trim()).filter(Boolean);
  } else if (typeof feiNumber === 'number') {
    feiArray = [String(feiNumber)];
  } else if (typeof feiNumber === 'string' && feiNumber.trim()) {
    // allow comma-separated list
    feiArray = feiNumber.split(',').map(s => s.trim()).filter(Boolean);
  }

  // Use FEI number(s) as cache key if available, otherwise use company name
  const cacheKey = feiArray.length > 0
    ? `fei-${feiArray.join(',')}`
    : companyName.trim().toLowerCase();

  /** L1: Memory cache */
  const memoryCached = inspectionCache.get(cacheKey);
  if (memoryCached && isCacheValid(memoryCached.lastFetched)) {
    return memoryCached;
  }

  /** L2: localStorage cache */
  const localCache = loadLocalCache();
  const localCached = localCache[cacheKey];

  if (localCached && isCacheValid(localCached.lastFetched)) {
    inspectionCache.set(cacheKey, localCached);
    return localCached;
  }

  /** API call: search by FEI if available, else exact phrase match on LegalName */
  let summary = await fetchFromFDA(companyName, feiArray.length > 0 ? feiArray : undefined);

  inspectionCache.set(cacheKey, summary);
  localCache[cacheKey] = summary;
  saveLocalCache(localCache);

  return summary;
}

/* ---------------- Cache Control ---------------- */

export function clearInspectionCache(companyName?: string, feiNumbers?: string | string[]): void {
  let cacheKey: string | undefined;

  const feiArr = Array.isArray(feiNumbers)
    ? feiNumbers.map(s => s.trim()).filter(Boolean)
    : feiNumbers?.trim()
      ? [feiNumbers.trim()]
      : [];

  if (feiArr.length > 0) {
    cacheKey = `fei-${feiArr.join(',')}`;
  } else if (companyName?.trim()) {
    cacheKey = companyName.trim().toLowerCase();
  }

  if (cacheKey) {
    inspectionCache.delete(cacheKey);

    const localCache = loadLocalCache();
    delete localCache[cacheKey];
    saveLocalCache(localCache);
  } else {
    inspectionCache.clear();
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
}

/* ---------------- Classification Colors ---------------- */

export function getClassificationColor(
  classificationCode: string
): { bg: string; text: string; border: string } {
  switch (classificationCode) {
    case 'OAI':
      return {
        bg: 'bg-red-100',
        text: 'text-red-700',
        border: 'border-red-300'
      };
    case 'VAI':
      return {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        border: 'border-yellow-300'
      };
    case 'NAI':
      return {
        bg: 'bg-green-100',
        text: 'text-green-700',
        border: 'border-green-300'
      };
    default:
      return {
        bg: 'bg-gray-100',
        text: 'text-gray-700',
        border: 'border-gray-300'
      };
  }
}
